import { MotionConfig, fadeUp, motion, stagger } from '@/lib/motion';
import { ArrowRight, CheckCircle2, XCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

import { useBrand } from '@/brand/brand-provider';
import { Button } from '@/components/ui/button';
import { Seo } from '@/lib/seo';


const ROWS = [
  { feature: 'Pricing model', us: 'Flat monthly — no per-minute billing', them: 'Per-minute + monthly plans' },
  { feature: 'After-hours coverage', us: '24/7 included in all plans', them: 'Available but costs more' },
  { feature: 'Setup time', us: 'Under 10 minutes', them: '2–5 business days for onboarding' },
  { feature: 'Knowledge base training', us: 'Upload your own docs — live instantly', them: 'Script written by AnswerConnect team' },
  { feature: 'Website chat included', us: 'Yes — same knowledge base', them: 'No — voice only' },
  { feature: 'Call transcripts', us: 'Full transcript + AI summary, all plans', them: 'Human call notes only' },
  { feature: 'AI-powered', us: 'Yes — LLM-based natural conversation', them: 'Human agents (virtual receptionists)' },
  { feature: 'Scales with call volume', us: 'Yes — flat monthly tiers with minutes included', them: 'Costs increase with minutes used' },
  { feature: 'Starting price', us: 'From $39/month', them: 'From ~$329/month (200 min)' },
];

export function VsAnswerConnectPage(): JSX.Element {
  const brand = useBrand();
  return (
    <MotionConfig reducedMotion="user">
      <Seo
        title={`${brand.displayName} vs AnswerConnect — AI Receptionist Comparison 2026`}
        description={`Comparing ${brand.displayName} vs AnswerConnect: pricing, features, setup time, and which is right for your business. AI vs human virtual receptionist.`}
        path="/compare/vs-answerconnect"
      />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute left-1/2 top-0 h-[400px] w-[800px] -translate-x-1/2 -translate-y-1/3 rounded-full bg-gradient-to-br from-[var(--brand-accent)]/15 via-[var(--brand-accent)]/5 to-transparent blur-3xl" />
        </div>
        <div className="container py-16 sm:py-20">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="mx-auto max-w-3xl text-center"
          >
            <motion.span
              variants={fadeUp}
              className="inline-flex items-center gap-2 rounded-full bg-[var(--brand-accent)]/10 px-3 py-1 text-xs font-semibold text-[var(--brand-accent)]"
            >
              Comparison
            </motion.span>
            <motion.h1
              variants={fadeUp}
              className="mt-4 text-4xl font-bold tracking-tight sm:text-5xl"
            >
              {brand.displayName} vs AnswerConnect
            </motion.h1>
            <motion.p
              variants={fadeUp}
              className="mt-4 text-lg text-muted-foreground"
            >
              AnswerConnect is a human virtual receptionist service. {brand.displayName} is AI-powered. Here&apos;s how they compare on price, features, and fit.
            </motion.p>
            <motion.div
              variants={fadeUp}
              className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center"
            >
              <Button asChild size="lg">
                <Link to="/signup">
                  Get started with {brand.displayName} <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/pricing">See pricing</Link>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Comparison table */}
      <section className="py-16 sm:py-20">
        <div className="container">
          <div className="mx-auto max-w-4xl">
            <h2 className="mb-8 text-center text-2xl font-bold">Feature comparison</h2>
            <div className="overflow-x-auto rounded-2xl border border-border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="py-4 pl-6 pr-4 text-left font-semibold">Feature</th>
                    <th className="py-4 px-4 text-left font-semibold text-[var(--brand-accent)]">
                      {brand.displayName}
                    </th>
                    <th className="py-4 pl-4 pr-6 text-left font-semibold text-muted-foreground">
                      AnswerConnect
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {ROWS.map(({ feature, us, them }, i) => (
                    <tr key={feature} className={i < ROWS.length - 1 ? 'border-b border-border/50' : ''}>
                      <td className="py-4 pl-6 pr-4 font-medium">{feature}</td>
                      <td className="py-4 px-4 text-[var(--brand-accent)]">{us}</td>
                      <td className="py-4 pl-4 pr-6 text-muted-foreground">{them}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* When to choose each */}
      <section className="border-t border-border py-16 sm:py-20">
        <div className="container">
          <div className="mx-auto grid max-w-4xl gap-8 sm:grid-cols-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="rounded-2xl border-2 border-[var(--brand-accent)]/40 bg-background p-7"
            >
              <h3 className="text-lg font-bold text-[var(--brand-accent)]">
                Choose {brand.displayName} if…
              </h3>
              <ul className="mt-5 space-y-3">
                {[
                  'You want 24/7 coverage without per-minute billing',
                  'Most calls are appointments, FAQs, and intake',
                  'You need to go live today, not in days',
                  'Your budget is under $200/month',
                  'You want website chat on the same knowledge base',
                  'You want consistent, predictable monthly costs',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                    {item}
                  </li>
                ))}
              </ul>
              <Button asChild className="mt-7 w-full">
                <Link to="/signup">Get started</Link>
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="rounded-2xl border border-border bg-muted/20 p-7"
            >
              <h3 className="text-lg font-bold text-muted-foreground">
                Consider AnswerConnect if…
              </h3>
              <ul className="mt-5 space-y-3">
                {[
                  'You need human judgment on complex, unpredictable calls',
                  'Your calls are high-stakes with significant nuance',
                  'You have a large budget ($500+/month)',
                  'You need outbound calling from live agents',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground/60" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>
    </MotionConfig>
  );
}
